import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import {
  EMPTY,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs';
import { isEqual } from '@comparit/core';
import { TaskContextService } from './task-context.service';

/**
 * Generic continuous-update engine for the task identity model: the same
 * pattern as the apps' storage sync (watch state, debounce, persist), but
 * persisting to the backend via `PUT task/{taskId}`. Fires only while a
 * taskId exists; each successful update extends the task's expiry
 * server-side.
 *
 * Apps extend this with their Sparte-specific payload composition and
 * generated API client — generated types must never be named here.
 */
@Injectable()
export abstract class TaskSyncBaseService<TUpdate> {
  protected readonly taskContext = inject(TaskContextService);
  private readonly destroyRef = inject(DestroyRef);

  /** Emits the full update payload on every meaningful state change. */
  protected abstract readonly updates$: Observable<TUpdate>;

  /** Sparte-specific transport for `PUT task/{taskId}`. */
  protected abstract sendUpdate(taskId: string, update: TUpdate): Observable<unknown>;

  private started = false;

  init(): void {
    if (this.started) return;
    this.started = true;

    combineLatest([this.updates$, this.taskContext.taskId$])
      .pipe(
        filter(([, taskId]) => taskId !== null),
        map(([update]) => update),
        debounceTime(1500),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        switchMap(update => {
          // Re-read: the context may have been cleared while debouncing
          // (e.g. a reset after a successful submit).
          const taskId = this.taskContext.taskId();
          if (!taskId) return EMPTY;
          return this.sendUpdate(taskId, update).pipe(
            // Background sync never surfaces errors. A 400 means the task
            // is expired or foreign — drop the context so no further
            // updates fire; anything else is transient and the next state
            // change retries.
            catchError((err: unknown) => {
              if (err instanceof HttpErrorResponse && err.status === 400) {
                this.taskContext.clear();
              }
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
